// src/components/common/PropertiesModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { formatFileSize, formatItemDate, removeTxtExtension } from '../../services/api.service';

const PropertiesModal = ({ isOpen, onClose, itemData, itemType = 'file' }) => {
  if (!isOpen || !itemData) return null;

  const isMobile = window.innerWidth <= 768;
  
  // Get type-specific configuration
  const getTypeConfig = () => {
    const configs = {
      file: {
        icon: 'fas fa-file-alt',
        title: 'File Properties',
        typeDescription: 'File',
        idLabel: 'File ID'
      },
      bankcard: {
        icon: 'fas fa-credit-card',
        title: 'Bank Card Properties',
        typeDescription: 'Bank Card Information',
        idLabel: 'Card ID'
      },
      phrase: {
        icon: 'fas fa-key',
        title: 'Secure Phrase Properties',
        typeDescription: 'Secure Phrase',
        idLabel: 'Phrase ID'
      },
      password: {
        icon: 'fas fa-lock',
        title: 'Password Properties',
        typeDescription: 'Password Entry',
        idLabel: 'Password ID'
      }
    };
    return configs[itemType] || configs.file;
  };

  const config = getTypeConfig();
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return formatItemDate(dateString) || 'Unknown';
    }
  };

  // Get display name
  const getDisplayName = () => {
    const name = itemData.name || itemData.originalName || 'Unknown';
    return itemType === 'file' ? name : removeTxtExtension(name);
  };

  // Get file extension for files
  const getFileExtension = (filename) => {
    if (itemType !== 'file' || !filename) return null;
    const isFolder = itemData.type === 'folder';
    if (isFolder) return 'Folder';
    const ext = filename.split('.').pop();
    return ext ? `.${ext.toUpperCase()}` : 'File';
  };

  // Get file type description for files
  const getFileTypeDescription = (filename) => {
    if (itemType !== 'file') return config.typeDescription;
    
    const isFolder = itemData.type === 'folder';
    if (isFolder) return 'File Folder';
    
    const ext = filename.split('.').pop()?.toLowerCase();
    const typeMap = {
      'pdf': 'PDF Document',
      'doc': 'Microsoft Word Document',
      'docx': 'Microsoft Word Document',
      'xls': 'Microsoft Excel Spreadsheet',
      'xlsx': 'Microsoft Excel Spreadsheet',
      'ppt': 'Microsoft PowerPoint Presentation',
      'pptx': 'Microsoft PowerPoint Presentation',
      'txt': 'Text Document',
      'jpg': 'JPEG Image',
      'jpeg': 'JPEG Image',
      'png': 'PNG Image',
      'gif': 'GIF Image',
      'bmp': 'Bitmap Image',
      'svg': 'SVG Vector Image',
      'mp4': 'MP4 Video',
      'avi': 'AVI Video',
      'mov': 'QuickTime Video',
      'mp3': 'MP3 Audio',
      'wav': 'WAV Audio',
      'zip': 'ZIP Archive',
      'rar': 'RAR Archive',
      '7z': '7-Zip Archive',
      'html': 'HTML Document',
      'css': 'CSS Stylesheet',
      'js': 'JavaScript File',
      'json': 'JSON File',
      'xml': 'XML Document'
    };
    
    return typeMap[ext] || 'File';
  };

  // Property row component
  const PropertyRow = ({ label, value, isLast = false }) => (
    <div 
      className={`property-row ${isMobile ? 'mobile' : ''}`}
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile ? '12px 0' : '8px 0',
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
        alignItems: isMobile ? 'flex-start' : 'center'
      }}
    >
      <div 
        style={{
          fontWeight: '600',
          color: '#495057',
          width: isMobile ? '100%' : '30%',
          marginBottom: isMobile ? '4px' : '0',
          fontSize: isMobile ? '14px' : '15px'
        }}
      >
        {label}:
      </div>
      <div 
        style={{
          color: '#6c757d',
          width: isMobile ? '100%' : '70%',
          wordBreak: 'break-word',
          fontSize: isMobile ? '14px' : '15px',
          lineHeight: '1.4'
        }}
      >
        {value}
      </div>
    </div>
  );

  const displayName = getDisplayName();

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block', 
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050
      }}
      onClick={onClose}
    >
      <div 
        className="modal-dialog modal-dialog-centered"
        style={{
          maxWidth: isMobile ? '95%' : '500px',
          margin: isMobile ? '10px auto' : '1.75rem auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ borderRadius: '8px', border: 'none' }}>
          {/* Modal Header */}
          <div 
            className="modal-header" 
            style={{ 
              borderBottom: '1px solid #dee2e6',
              padding: isMobile ? '16px 20px' : '20px 24px',
              backgroundColor: '#f8f9fa'
            }}
          >
            <div className="d-flex align-items-center">
              <div style={{ marginRight: '12px' }}>
                {itemData.thumbnail ? (
                  <img 
                    src={itemData.thumbnail} 
                    alt={displayName} 
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }} 
                  />
                ) : (
                  <i 
                    className={config.icon} 
                    style={{ 
                      color: '#00CC5C', 
                      fontSize: '28px' 
                    }}
                  />
                )}
              </div>
              <div>
                <h5 
                  className="modal-title mb-0" 
                  style={{ 
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    color: '#212529'
                  }}
                >
                  {config.title}
                </h5>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#6c757d',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Modal Body */}
          <div 
            className="modal-body" 
            style={{ 
              padding: isMobile ? '16px 20px' : '20px 24px',
              maxHeight: isMobile ? '70vh' : '60vh',
              overflowY: 'auto'
            }}
          >
            {/* Item Name Section */}
            <div 
              style={{ 
                textAlign: 'center', 
                marginBottom: '24px',
                padding: isMobile ? '16px' : '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                {itemData.thumbnail ? (
                  <img 
                    src={itemData.thumbnail} 
                    alt={displayName} 
                    style={{ 
                      width: isMobile ? '48px' : '64px', 
                      height: isMobile ? '48px' : '64px', 
                      objectFit: 'cover',
                      borderRadius: '8px'
                    }} 
                  />
                ) : (
                  <i 
                    className={config.icon} 
                    style={{ 
                      color: '#00CC5C', 
                      fontSize: isMobile ? '36px' : '48px' 
                    }}
                  />
                )}
              </div>
              <div 
                style={{ 
                  fontWeight: '600', 
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#212529',
                  wordBreak: 'break-word',
                  lineHeight: '1.3'
                }}
              >
                {displayName}
              </div>
            </div>

            {/* Properties List */}
            <div style={{ backgroundColor: 'white' }}>
              <PropertyRow 
                label="Name" 
                value={displayName} 
              />
              
              <PropertyRow 
                label="Type" 
                value={getFileTypeDescription(itemData.name)} 
              />
              
              {/* Extension only for files */}
              {itemType === 'file' && getFileExtension(itemData.name) && (
                <PropertyRow 
                  label="Extension" 
                  value={getFileExtension(itemData.name)} 
                />
              )}
              
              {/* Size (hide for folders) */}
              {itemData.size !== undefined && !(itemType === 'file' && itemData.type === 'folder') && (
                <PropertyRow 
                  label="Size" 
                  value={formatFileSize(itemData.size)} 
                />
              )}
              
              {/* Location/Path only for files */}
              {itemType === 'file' && itemData.path && (
                <PropertyRow 
                  label="Location" 
                  value={itemData.path} 
                />
              )}
              
              {/* Modified date */}
              {(itemData.lastEdited || itemData.lastModified) && (
                <PropertyRow 
                  label="Modified" 
                  value={formatDisplayDate(itemData.lastEdited || itemData.lastModified)} 
                />
              )}
              
              {/* Created date */}
              {itemData.dateCreated && (
                <PropertyRow 
                  label="Created" 
                  value={formatDisplayDate(itemData.dateCreated)} 
                />
              )}
              
              {/* Owner only for files */}
              {itemType === 'file' && itemData.owner && (
                <PropertyRow 
                  label="Owner" 
                  value={itemData.owner} 
                />
              )}
              
              {/* ID */}
              {itemData.id && (
                <PropertyRow 
                  label={config.idLabel} 
                  value={itemData.id} 
                  isLast={true}
                />
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div 
            className="modal-footer" 
            style={{ 
              borderTop: '1px solid #dee2e6',
              padding: isMobile ? '12px 20px' : '16px 24px',
              backgroundColor: '#f8f9fa'
            }}
          >
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
              style={{
                minWidth: '80px',
                padding: '8px 16px',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

PropertiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itemData: PropTypes.object,
  itemType: PropTypes.oneOf(['file', 'bankcard', 'phrase', 'password']).isRequired
};

export default PropertiesModal;